import { useCallback, useEffect, useState } from 'react';
import { bffQuery, bffMutation } from '../lib/bffClient';

const FIELDS = 'channels templateId defaultBody isDefault updatedAt template { id title body } rescheduleTemplateId defaultRescheduleBody rescheduleTemplate { id title body }';

export const APPOINTMENT_NOTIFICATION_SETTINGS = `
  query AppointmentNotificationSettings {
    appointmentNotificationSettings { ${FIELDS} }
  }
`;

const UPDATE = `
  mutation UpdateAppointmentNotificationSettings($input: AppointmentNotificationSettingsInput!) {
    updateAppointmentNotificationSettings(input: $input) { ${FIELDS} }
  }
`;

/**
 * «تنظیمات › اعلان‌ها» — which channels «ثبت نوبت» ticks by default, and the sentence.
 *
 * Read by two callers with different rights, which is why it is one hook: the settings
 * page edits it (`clinic.settings.manage`), and every booking dialog merely reads it to
 * prefill its ticks (`appointments.manage`). A read the caller may not make, or one
 * against a BFF older than the field, leaves `settings` null — which the dialog reads
 * as «nothing ticked by default», never as an error in front of the desk.
 *
 * The save replaces both fields at once. The server writes them through the booking
 * policy's mask, so it cannot disturb any other rule on that row.
 */
export function useAppointmentNotificationSettings({ enabled = true } = {}) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    if (!enabled) { setLoading(false); return; }
    setLoading(true);
    try {
      const data = await bffQuery(APPOINTMENT_NOTIFICATION_SETTINGS);
      setSettings(data?.appointmentNotificationSettings || null);
      setError(null);
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => { reload(); }, [reload]);

  /**
   * Saves `{ channels, templateId, rescheduleTemplateId }`; returns what the server
   * wrote. `rescheduleTemplateId` left undefined is not sent, which the server reads as
   * «unchanged».
   */
  const save = useCallback(async ({ channels, templateId, rescheduleTemplateId }) => {
    const input = { channels: channels || [], templateId: templateId || null };
    if (rescheduleTemplateId !== undefined) input.rescheduleTemplateId = rescheduleTemplateId || null;
    const data = await bffMutation(UPDATE, { input });
    const next = data?.updateAppointmentNotificationSettings || null;
    if (next) setSettings(next);
    return next;
  }, []);

  return { settings, loading, error, reload, save };
}

export default useAppointmentNotificationSettings;
